import { useCountByStatus } from "@/api/auction/auction";
import { PagingParameters } from "@/api/dtos";
import {
  useDeleteComplexLink,
  useGetComplexRelationFields,
  useGetNotRelatedResource,
} from "@/api/related-fields";
import {
  artworkColumnsFormatted,
  artworkColumnsVisibility,
  getArtworkForComplexLink,
} from "@/config/dashboard/artwork";
import { generateTable } from "@/config/dashboard/general";
import { FieldConfig } from "@/config/forms/general";
import { Artwork, ArtworkInAuction, Auction } from "@/lib/types";
import { ColumnDef, Row } from "@tanstack/react-table";
import { ChevronLeft, PlusIcon, Trash2Icon } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ShowElements from "../drawer/ShowElements";
import { LinkableType } from "../LinksManagement";
import { HtmlCell, ImageCell, TextCell } from "../table/CustomCell";
import { Button } from "../ui/button";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";
import ArtworkInAuctionDrawer from "./drawer/ArtworkInAuctionDrawer";

interface ComplexLinkProps {
  id: string;
  artwork: Artwork;
  fields: FieldConfig[] | undefined;
  type: LinkableType;
  auction: Auction;
}

const columnsAuction = [
  {
    accessorKey: "auction.ventesDescriptionNumeroVente",
    header: "ventesDescriptionNumeroVente",
    cell: ({ row }: { row: Row<ArtworkInAuction> }) => {
      return (
        <TextCell value={row.original.auction.ventesDescriptionNumeroVente} />
      );
    },
  },
  {
    accessorKey: "vignette",
    header: "",
    size: 100,
    cell: ({ row }: { row: Row<ArtworkInAuction> }) => {
      return (
        <ImageCell
          value={""}
          attachments={row.original.attachments ?? []}
          domain={"auction"}
        />
      );
    },
  },
  {
    accessorKey: "auction.ventesDescriptionCommissaires",
    header: "ventesDescriptionCommissaires",
    cell: ({ row }: { row: Row<ArtworkInAuction> }) => {
      return (
        <TextCell value={row.original.auction.ventesDescriptionCommissaires} />
      );
    },
  },
  {
    accessorKey: "auction.ventesDescriptionDate",
    header: "ventesDescriptionDate",
    cell: ({ row }: { row: Row<ArtworkInAuction> }) => {
      return <TextCell value={row.original.auction.ventesDescriptionDate} />;
    },
  },
  {
    accessorKey: "auction.ventesDescriptionVille",
    header: "ventesDescriptionVille",
    cell: ({ row }: { row: Row<ArtworkInAuction> }) => {
      return <TextCell value={row.original.auction.ventesDescriptionVille} />;
    },
  },
  {
    accessorKey: "auction.ventesDescriptionPays",
    header: "ventesDescriptionPays",
    cell: ({ row }: { row: Row<ArtworkInAuction> }) => {
      return <TextCell value={row.original.auction.ventesDescriptionPays} />;
    },
  },
];
/**
 * ArtworkLinks component
 * @returns {JSX.Element}
 */
const ArtworkInAuctionLinks = ({
  id,
  artwork,
  fields,
  type,
  auction,
}: ComplexLinkProps) => {
  const { t } = useTranslation();

  const { data: relatedData } = useGetComplexRelationFields(
    id,
    "artworkInAuctions",
    type,
  );

  const [searchContent, setSearchContent] = useState("");

  const { mutate: deleteArtworkInAuction } = useDeleteComplexLink(
    id,
    "artworkInAuctions",
    type,
  );

  const [isAddMode, setIsAddMode] = useState(false);

  const handleDeleteRelation = async (id: string) => {
    await deleteArtworkInAuction({
      id,
      relation: "artworkInAuction",
    });
  };

  const columns: ColumnDef<ArtworkInAuction, any>[] = useMemo(() => {
    const columnsArtwork = getArtworkForComplexLink() as ColumnDef<
      ArtworkInAuction,
      any
    >[];
    return [
      ...(type === "artwork"
        ? (columnsAuction.map((column) => ({
            ...column,
            header: typeof column.header === "string" ? t(column.header) : "",
          })) as ColumnDef<ArtworkInAuction, any>[])
        : columnsArtwork.map((column) => ({
            ...column,
            header: typeof column.header === "string" ? t(column.header) : "",
          }))),
      {
        accessorKey: "oeuvresDansVentesDescriptionNumeroLot",
        header: t("auctionLotNumber"),
      },
      {
        accessorKey: "actions",
        header: "",
        size: 100,
        cell: ({ row }: { row: Row<ArtworkInAuction> }) => {
          const data = row.original as ArtworkInAuction;
          return (
            <div className="flex items-center gap-1 justify-end mr-2">
              <Button
                className="font-semibold flex gap-2 p-1"
                variant="ghost"
                type="button"
                onClick={() => handleDeleteRelation(data.id)}
              >
                <Trash2Icon className="w-5 h-5 cursor-pointer text-gray-500" />
              </Button>
              <ArtworkInAuctionDrawer
                key={row.original.id}
                fields={fields}
                data={data}
                queryId={id}
                type={type}
              />
              {type === "auction" && (
                <ShowElements
                  type="artwork"
                  id={row.original.artwork.id}
                  elements={[
                    {
                      label: t("oeuvresDescriptionTitre"),
                      value: row.original.artwork.oeuvresDescriptionTitre,
                      type: "text",
                    },
                    {
                      label: t("oeuvresDescriptionNumeroEnregistrement"),
                      value:
                        row.original.artwork
                          .oeuvresDescriptionNumeroEnregistrement,
                      type: "text",
                    },
                    {
                      label: t("oeuvresDescriptionResume"),
                      value: row.original.artwork.oeuvresDescriptionResume,
                      type: "richtext",
                    },
                    {
                      label: t("attachments"),
                      value: row.original.artwork.attachments,
                      type: "image",
                    },
                  ]}
                />
              )}
              {type === "artwork" && (
                <ShowElements
                  type="auction"
                  id={row.original.auction.id}
                  elements={[
                    {
                      label: t("ventesDescriptionNumeroVente"),
                      value: row.original.auction.ventesDescriptionNumeroVente,
                      type: "text",
                    },
                    {
                      label: t("ventesDescriptionResume"),
                      value: row.original.auction.ventesDescriptionResume,
                      type: "richtext",
                    },
                  ]}
                />
              )}
            </div>
          );
        },
      },
    ];
  }, [isAddMode]);

  const datatableConf = useMemo(() => {
    if (type === "artwork") {
      return (
        <ArtworkToAuctionLinks
          id={id}
          relatedData={relatedData}
          fields={fields}
          artwork={artwork}
          isAddMode={isAddMode}
          columnsLink={columns}
          searchContent={searchContent}
        />
      );
    } else {
      return (
        <AuctionToArtworkLinks
          id={id}
          relatedData={relatedData}
          fields={fields}
          auction={auction}
          isAddMode={isAddMode}
          columnsLink={columns}
          searchContent={searchContent}
        />
      );
    }
  }, [
    type,
    id,
    relatedData,
    fields,
    artwork,
    isAddMode,
    columns,
    searchContent,
    auction,
  ]);

  return (
    <div className="w-full">
      {isAddMode && (
        <div
          className="flex items-center gap-1 mb-4 cursor-pointer"
          onClick={() => setIsAddMode(false)}
        >
          <ChevronLeft className="w-3 h-3 text-gray-300" />
          <p className="text-sm text-[#006EAB] hover:text-[#005D8D]">
            {t("relatedRecords")}
          </p>
        </div>
      )}
      <div className="flex justify-between items-center mb-2">
        <h2 className="text-lg font-semibold">
          {type !== "artwork" ? t("artworksRelated") : t("auctionsRelated")}
        </h2>
        {!isAddMode && (
          <Button variant="outline" onClick={() => setIsAddMode(true)}>
            <PlusIcon className="w-4 h-4 mr-2" />
            {t("add")}
          </Button>
        )}
        {isAddMode && (
          <div className="flex items-center gap-2">
            <Input
              placeholder={t("search")}
              className="w-80"
              value={searchContent}
              onChange={(e) => setSearchContent(e.target.value)}
            />
            {/* <Button variant="default" onClick={() => setIsAddMode(false)}>
              <PlusIcon className="w-4 h-4 mr-2" />
              {t("newArtwork")}
            </Button> */}
          </div>
        )}
      </div>
      {datatableConf}
    </div>
  );
};

export type LinksTableProps<T> = {
  id: string;
  relatedData: T[];
  fields: FieldConfig[] | undefined;
  isAddMode: boolean;
  columnsLink: ColumnDef<T, any>[];
  searchContent: string;
};

type ArtworkToAuctionLinksProps = LinksTableProps<ArtworkInAuction> & {
  artwork: Artwork | undefined;
};

type AuctionToArtworkLinksProps = LinksTableProps<ArtworkInAuction> & {
  auction: Auction | undefined;
};

//Composant pour Oeuvres
const ArtworkToAuctionLinks = ({
  id,
  relatedData,
  fields,
  artwork,
  isAddMode,
  columnsLink,
  searchContent,
}: ArtworkToAuctionLinksProps) => {
  const { t } = useTranslation();
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  const { data: countByStatus } = useCountByStatus();
  const totalSize = countByStatus?.published + countByStatus?.draft;
  const { data: notAlreadyRelatedAuctions } = useGetNotRelatedResource<Auction>(
    {
      resultType: "auction",
      resourceId: id,
      pagination: getPagination,
      searchQuery: searchContent,
    },
  );

  const columnsAuctions: ColumnDef<Auction, any>[] = useMemo(() => {
    return [
      {
        accessorKey: "ventesDescriptionNumeroVente",
        header: t("ventesDescriptionNumeroVente"),
        cell: ({ row }) => {
          return <TextCell value={row.original.ventesDescriptionNumeroVente} />;
        },
      },
      {
        accessorKey: "vignette",
        header: "",
        size: 100,
        cell: ({ row }: { row: Row<Auction> }) => {
          return (
            <ImageCell
              value={""}
              attachments={row.original.attachments ?? []}
              domain={"auction"}
            />
          );
        },
      },
      {
        accessorKey: "ventesDescriptionTitre",
        header: t("ventesDescriptionTitre"),
        size: 500,
        cell: (props) => {
          return <HtmlCell value={props.getValue()} />;
        },
      },
      {
        accessorKey: "ventesDescriptionDate",
        header: t("ventesDescriptionDate"),
        size: 100,
        cell: ({ row }) => {
          return <TextCell value={row.original.ventesDescriptionDate} />;
        },
      },
      {
        accessorKey: "ventesDescriptionVille",
        header: t("ventesDescriptionVille"),
        cell: ({ row }) => {
          return <TextCell value={row.original.ventesDescriptionVille} />;
        },
      },
      {
        accessorKey: "ventesDescriptionPays",
        header: t("ventesDescriptionPays"),
        cell: ({ row }) => {
          return <TextCell value={row.original.ventesDescriptionPays} />;
        },
      },
      {
        accessorKey: "actions",
        header: "",
        size: 100,
        cell: ({ row }) => {
          const data = row.original as Auction;
          if (!artwork) return null;
          return (
            <div className="flex items-center gap-1 justify-end mr-2">
              <ShowElements
                type="auction"
                id={row.original.id}
                elements={[
                  {
                    label: t("ventesDescriptionNumeroVente"),
                    value: row.original.ventesDescriptionNumeroVente,
                    type: "text",
                  },
                  {
                    label: t("ventesDescriptionResume"),
                    value: row.original.ventesDescriptionResume,
                    type: "richtext",
                  },
                ]}
              />
              <ArtworkInAuctionDrawer
                fields={fields}
                key={row.original.id}
                queryId={artwork.id}
                type={"artwork"}
                data={{
                  id: "",
                  auction: data,
                  artwork: artwork,
                  oeuvresDansVentesDescriptionNumeroLot: "",
                  oeuvresDansVentesDescriptionNotes: "",
                  oeuvresDansVentesDescriptionReproduitPage: "",
                  oeuvresDansVentesDescriptionEstimationHaute: "",
                  oeuvresDansVentesDescriptionEstimationBasse: "",
                  oeuvresDansVentesDescriptionVendu: false,
                  oeuvresDansVentesDescriptionResultatNonListe: false,
                  oeuvresDansVentesDescriptionNonVenu: false,
                  oeuvresDansVentesDescriptionNonVendu: false,
                  oeuvresDansVentesDescriptionNonReproduit: false,
                  oeuvresDansVentesDescriptionRetire: false,
                  oeuvresDansVentesDescriptionMiseAPrix: "",
                  oeuvresDansVentesDescriptionPrixMarteau: "",
                  oeuvresDansVentesDescriptionPrixAvecFrais: "",
                }}
              />
            </div>
          );
        },
      },
    ];
  }, []);

  const datatableConf = useMemo(
    () =>
      isAddMode ? (
        <DataTable
          columns={columnsAuctions}
          data={notAlreadyRelatedAuctions?.data ?? []}
          pagination={
            isAddMode
              ? {
                  pagination: getPagination,
                  setPage: setPagination,
                  totalSize: notAlreadyRelatedAuctions?.totalSize ?? 0,
                }
              : undefined
          }
          options={{
            enableColumnResizing: true,
          }}
        />
      ) : (
        <DataTable
          columns={columnsLink}
          data={relatedData ?? []}
          pagination={
            isAddMode
              ? {
                  pagination: getPagination,
                  setPage: setPagination,
                  totalSize: totalSize,
                }
              : undefined
          }
          options={{
            enableColumnResizing: true,
          }}
        />
      ),
    [isAddMode, notAlreadyRelatedAuctions, relatedData],
  );

  return datatableConf;
};

//Composant pour ventes
const AuctionToArtworkLinks = ({
  id,
  relatedData,
  fields,
  auction,
  isAddMode,
  columnsLink,
  searchContent,
}: AuctionToArtworkLinksProps) => {
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  const { data: notAlreadyRelatedArtworks } = useGetNotRelatedResource<Artwork>(
    {
      resultType: "artwork",
      resourceId: id,
      pagination: getPagination,
      searchQuery: searchContent,
    },
  );

  const { t } = useTranslation();
  const columnsArtworks: ColumnDef<Artwork, any>[] = useMemo(() => {
    return [
      ...generateTable(artworkColumnsFormatted, "artwork"),
      {
        accessorKey: "actions",
        header: "",
        size: 100,
        cell: ({ row }) => {
          const data = row.original as Artwork;
          if (!auction) return null;
          return (
            <div className="flex items-center gap-1 justify-end mr-2">
              <ShowElements
                type="artwork"
                id={row.original.id}
                elements={[
                  {
                    label: t("oeuvresDescriptionTitre"),
                    value: row.original.oeuvresDescriptionTitre,
                    type: "text",
                  },
                  {
                    label: t("oeuvresDescriptionNumeroEnregistrement"),
                    value: row.original.oeuvresDescriptionNumeroEnregistrement,
                    type: "text",
                  },
                  {
                    label: t("oeuvresDescriptionResume"),
                    value: row.original.oeuvresDescriptionResume,
                    type: "richtext",
                  },
                  {
                    label: t("attachments"),
                    value: row.original.attachments,
                    type: "image",
                  },
                ]}
              />
              <ArtworkInAuctionDrawer
                fields={fields}
                key={row.original.id}
                queryId={auction.id}
                type={"auction"}
                data={{
                  id: "",
                  auction: auction,
                  artwork: data,
                  oeuvresDansVentesDescriptionNumeroLot: "",
                  oeuvresDansVentesDescriptionNotes: "",
                  oeuvresDansVentesDescriptionReproduitPage: "",
                  oeuvresDansVentesDescriptionEstimationHaute: "",
                  oeuvresDansVentesDescriptionEstimationBasse: "",
                  oeuvresDansVentesDescriptionVendu: false,
                  oeuvresDansVentesDescriptionResultatNonListe: false,
                  oeuvresDansVentesDescriptionNonVenu: false,
                  oeuvresDansVentesDescriptionNonVendu: false,
                  oeuvresDansVentesDescriptionNonReproduit: false,
                  oeuvresDansVentesDescriptionRetire: false,
                  oeuvresDansVentesDescriptionMiseAPrix: "",
                  oeuvresDansVentesDescriptionPrixMarteau: "",
                  oeuvresDansVentesDescriptionPrixAvecFrais: "",
                }}
              />
            </div>
          );
        },
      },
    ];
  }, []);

  const datatableConf = useMemo(
    () =>
      isAddMode ? (
        <DataTable
          columns={columnsArtworks}
          data={notAlreadyRelatedArtworks?.data ?? []}
          pagination={
            isAddMode
              ? {
                  pagination: getPagination,
                  setPage: setPagination,
                  totalSize: notAlreadyRelatedArtworks?.totalSize ?? 0,
                }
              : undefined
          }
          options={{
            state: {
              columnVisibility: {
                ...artworkColumnsVisibility,
                oeuvresDescriptionVignette: true,
                oeuvresPublicationStatus: false,
              },
            },
            enableColumnResizing: true,
          }}
        />
      ) : (
        <DataTable
          columns={columnsLink}
          data={relatedData ?? []}
          pagination={
            isAddMode
              ? {
                  pagination: getPagination,
                  setPage: setPagination,
                  totalSize: notAlreadyRelatedArtworks?.totalSize ?? 0,
                }
              : undefined
          }
          options={{
            enableColumnResizing: true,
          }}
        />
      ),
    [
      columnsArtworks,
      columnsLink,
      getPagination,
      isAddMode,
      notAlreadyRelatedArtworks?.data,
      notAlreadyRelatedArtworks?.totalSize,
      relatedData,
    ],
  );

  return datatableConf;
};
export default ArtworkInAuctionLinks;
